import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import {
  faBorderBottom,
  faBorderLeft,
  faBorderRight,
  faBorderTop,
} from "@fortawesome/pro-regular-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  EMAIL_DOCUMENT_DEFAULT_BAND_BORDER_COLOR,
  EMAIL_DOCUMENT_MAX_BORDER_WIDTH,
} from "../../schema/emailDocumentContract";
import type { BorderStyle, Padding } from "../../domain/types";
import {
  bandBorderAfterWidthChange,
  bandBorderHasVisibleWidth,
  bandBorderResolveColor,
  defaultSurfaceBorder,
} from "../../operations/emailBuilderBandBorder";
import { emailBuilderSpacingIsUniform } from "../../shell/panel/blockSettings/emailBuilderBlockSettingsSpacing";
import { EmailBuilderColorOverrideField } from "../../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import { useEmailBuilderSettingsOverlayPortalId } from "../../shell/canvas/settings/emailBuilderSettingsOverlayPortal";
import { isSurfaceBorderInactive } from "../settingsSectionActivation";
import { EmailBuilderSettingsFieldGrid, EmailBuilderSettingsFieldGridRow } from "./EmailBuilderSettingsFieldGrid";
import { EmailBuilderSettingsIconNumberField } from "./EmailBuilderSettingsIconNumberField";
import { EmailBuilderSettingsRadioGroup } from "./EmailBuilderSettingsRadioGroup";
import { useEmailBuilderSettingsPanelScrollOnChange } from "../useEmailBuilderSettingsPanelScrollOnChange";

const { Option } = Select;

const EMAIL_BUILDER_BORDER_SIDES = ["top", "right", "bottom", "left"] as const;

const EMAIL_BUILDER_BORDER_SIDE_LABELS: Record<(typeof EMAIL_BUILDER_BORDER_SIDES)[number], string> = {
  top: "Top",
  right: "Right",
  bottom: "Bottom",
  left: "Left",
};

const EMAIL_BUILDER_BORDER_SIDE_ICONS: Record<(typeof EMAIL_BUILDER_BORDER_SIDES)[number], IconDefinition> = {
  top: faBorderTop,
  right: faBorderRight,
  bottom: faBorderBottom,
  left: faBorderLeft,
};

type TBorderStyleOption = BorderStyle["style"];

type TBorderMode = "uniform" | "custom";

type EmailBuilderSettingsBorderFieldsProps = {
  border: BorderStyle;
  onChange: (border: BorderStyle, historyGroupId?: string | null) => void;
  hideCustomizeSides?: boolean;
  styles?: readonly TBorderStyleOption[];
  colorOverride?: {
    hasOverride: boolean;
    onReset: () => void;
  };
};

const BORDER_STYLE_OPTIONS: TBorderStyleOption[] = ["none", "solid", "dashed", "dotted"];

const BORDER_STYLE_LABELS: Record<TBorderStyleOption, string> = {
  none: "None",
  solid: "Solid",
  dashed: "Dashed",
  dotted: "Dotted",
};

function resolveBorderType(border: BorderStyle): TBorderStyleOption {
  if (!bandBorderHasVisibleWidth(border.width) || border.style === "none") {
    return "none";
  }
  return border.style;
}

function uniformBorderWidth(width: Padding): number {
  return width.top;
}

function resolveBorderMode(width: Padding, hideCustomizeSides: boolean): TBorderMode {
  if (hideCustomizeSides) {
    return "uniform";
  }
  return emailBuilderSpacingIsUniform(width) ? "uniform" : "custom";
}

export function EmailBuilderSettingsBorderFields(props: EmailBuilderSettingsBorderFieldsProps) {
  const { t } = useTranslation();
  const overlayPortalId = useEmailBuilderSettingsOverlayPortalId();
  const { border, onChange, hideCustomizeSides = false, colorOverride } = props;
  const styleOptions = props.styles ?? BORDER_STYLE_OPTIONS;
  const width = border.width;
  const borderType = resolveBorderType(border);
  const borderDisabled = borderType === "none";
  const isUniform = emailBuilderSpacingIsUniform(width);
  const [mode, setMode] = useState<TBorderMode>(() => resolveBorderMode(width, hideCustomizeSides));
  const wasInactiveRef = useRef(isSurfaceBorderInactive(border));
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useEmailBuilderSettingsPanelScrollOnChange(sectionRef, mode);

  useEffect(() => {
    const isInactive = isSurfaceBorderInactive(border);
    if (wasInactiveRef.current && !isInactive) {
      setMode(resolveBorderMode(width, hideCustomizeSides));
    }
    wasInactiveRef.current = isInactive;
  }, [border, width, hideCustomizeSides]);

  const resolvedColor = bandBorderResolveColor(border);
  const hasColorOverride =
    colorOverride?.hasOverride ??
    (typeof border.color === "string" && border.color !== EMAIL_DOCUMENT_DEFAULT_BAND_BORDER_COLOR);

  function commitWidth(nextWidth: Padding, historyGroupId?: string | null) {
    onChange(bandBorderAfterWidthChange(border, nextWidth), historyGroupId);
  }

  function handleTypeChange(nextType: TBorderStyleOption) {
    if (nextType === "none") {
      onChange(defaultSurfaceBorder(), null);
      setMode("uniform");
      return;
    }

    const nextWidth = bandBorderHasVisibleWidth(width)
      ? width
      : { top: 1, right: 1, bottom: 1, left: 1 };

    onChange(
      bandBorderAfterWidthChange(
        {
          ...border,
          style: nextType,
        },
        nextWidth,
      ),
      null,
    );
  }

  return (
    <div ref={sectionRef} className="email-builder-settings-border-fields">
      {!hideCustomizeSides ? (
        <EmailBuilderSettingsRadioGroup
          name="email-builder-border-mode"
          value={mode}
          options={[
            { value: "uniform", label: t("All sides") },
            { value: "custom", label: t("Customize each side") },
          ]}
          ariaLabel={t("Border mode")}
          onChange={nextMode => {
            setMode(nextMode);
          }}
        />
      ) : null}

      <div className="email-builder-settings-border-fields__type-row">
        <div className="email-builder-settings-border-fields__type">
          <label className="email-builder-settings-icon-number-field__label">{t("Type")}</label>
          <Select
            key={borderType}
            defaultValue={borderType}
            onChange={(nextValue: TBorderStyleOption) => handleTypeChange(nextValue)}
            usePortal
            portalId={overlayPortalId}
          >
            {styleOptions.map(option => (
              <Option key={option} value={option}>
                {t(BORDER_STYLE_LABELS[option])}
              </Option>
            ))}
          </Select>
        </div>
        <EmailBuilderSettingsIconNumberField
          label={t("Thickness (px)")}
          value={uniformBorderWidth(width)}
          min={0}
          max={EMAIL_DOCUMENT_MAX_BORDER_WIDTH}
          disabled={borderDisabled}
          mixed={!isUniform}
          onChange={(next, historyGroupId) => {
            commitWidth({ top: next, right: next, bottom: next, left: next }, historyGroupId);
          }}
        />
      </div>

      <EmailBuilderColorOverrideField
        label={t("Color")}
        color={resolvedColor}
        hasOverride={hasColorOverride}
        showFieldLabel={false}
        onChange={(color, historyGroupId) => {
          onChange({ ...border, color }, historyGroupId);
        }}
        onReset={() => {
          if (colorOverride) {
            colorOverride.onReset();
            return;
          }
          onChange({
            ...border,
            color: EMAIL_DOCUMENT_DEFAULT_BAND_BORDER_COLOR,
          });
        }}
      />

      {!borderDisabled && !hideCustomizeSides && mode === "custom" ? (
        <EmailBuilderSettingsFieldGrid>
          <EmailBuilderSettingsFieldGridRow>
            {(["top", "bottom"] as const).map(side => (
              <EmailBuilderSettingsIconNumberField
                key={side}
                label={t("{{side}} (px)", { side: t(EMAIL_BUILDER_BORDER_SIDE_LABELS[side]) })}
                icon={EMAIL_BUILDER_BORDER_SIDE_ICONS[side]}
                value={width[side]}
                min={0}
                max={EMAIL_DOCUMENT_MAX_BORDER_WIDTH}
                onChange={(next, historyGroupId) => {
                  commitWidth({ ...width, [side]: next }, historyGroupId);
                }}
              />
            ))}
          </EmailBuilderSettingsFieldGridRow>
          <EmailBuilderSettingsFieldGridRow>
            {(["left", "right"] as const).map(side => (
              <EmailBuilderSettingsIconNumberField
                key={side}
                label={t("{{side}} (px)", { side: t(EMAIL_BUILDER_BORDER_SIDE_LABELS[side]) })}
                icon={EMAIL_BUILDER_BORDER_SIDE_ICONS[side]}
                value={width[side]}
                min={0}
                max={EMAIL_DOCUMENT_MAX_BORDER_WIDTH}
                onChange={(next, historyGroupId) => {
                  commitWidth({ ...width, [side]: next }, historyGroupId);
                }}
              />
            ))}
          </EmailBuilderSettingsFieldGridRow>
        </EmailBuilderSettingsFieldGrid>
      ) : null}
    </div>
  );
}
